import {InjectionToken, Signal} from '@angular/core';
import {Observable} from 'rxjs';
import {
  Appointment,
  AppointmentClash,
  AppointmentNotification,
  AppointmentType,
  CalendarAppointment,
  CalendarAppointmentPatient,
  CreatedPatient,
  Patient,
  PatientFlagEntity,
  PracticeLocation,
  PracticePractitioner,
  PracticeSettings,
  QueryPatient,
  SelectOption,
  UserSettings
} from '@xmedical/core';

export type CalendarPatient = Patient | QueryPatient | CalendarAppointmentPatient;

/**
 * Data provider abstraction for the calendar library.
 * Each consuming app provides an implementation that bridges
 * the calendar to that app's specific API endpoints and stores.
 */
export interface CalendarDataProvider {
  // State signals
  practiceLocations: Signal<PracticeLocation[]>;
  practicePractitioners: Signal<PracticePractitioner[]>;
  selectedPracticeId: Signal<number | null>;
  appointmentTypes: Signal<AppointmentType[]>;
  practiceSettings: Signal<PracticeSettings | null>;
  /** Current user's personal settings. Optional — consumers outside a practice user context (e.g. organisation-portal) may omit. */
  userSettings?: Signal<UserSettings | null>;
  isReady: Signal<boolean>;
  /** Initial appointment type IDs to display. Empty array = show all. */
  defaultVisibleAppointmentTypeIds: Signal<number[]>;
  /** The current user's linked practitioner ID, if any. */
  linkedPracticePractitionerId?: Signal<number | null>;
  /**
   * Practice-wide patient flag definitions. Optional — when provided, the calendar
   * can patch flag changes onto displayed appointments in place instead of firing
   * a full appointment reload. Consumers that don't have a flag source (e.g.
   * organisation-portal) can omit this and fall back to the full-refresh behaviour.
   */
  patientFlags?: Signal<PatientFlagEntity[]>;

  // Data operations (practice-scoped by the implementation)
  loadAppointmentsByDay(date: Date, locationId: number | null, practitionerId: number | null): Observable<CalendarAppointment[]>;
  loadAppointments(startDate: string, endDate: string, locationId: number | null, practitionerId: number | null): Observable<CalendarAppointment[]>;
  deleteAppointment(appointmentId: number): Observable<boolean>;
  updateAppointment(appointment: Appointment): Observable<boolean>;
  createAppointment(appointment: Appointment): Observable<boolean>;

  /**
   * Load the notification history for an appointment. Optional — only practice-scoped
   * apps (xmedical-app) expose a notifications endpoint; the organisation-portal operates
   * above practice level (no X-Practice-Id) and omits this. When absent, the calendar
   * hides the "Notification Log" entry points so they never call an unreachable endpoint.
   */
  loadAppointmentNotifications?(appointmentId: number): Observable<AppointmentNotification[]>;

  /**
   * This practitioner's existing appointments overlapping the given time slot, across
   * ALL locations (the caller classifies same-vs-other location from each clash's
   * locationId). Optional — only practice-scoped apps (xmedical-app) expose the clash
   * endpoint; the organisation-portal omits it. When absent, the calendar hides the
   * clash banner. `start`/`end` are zone-less practice wall-clock strings. Non-blocking —
   * used only to warn; never gates save.
   */
  findClashes?(
    practitionerId: number,
    start: string,
    end: string,
    excludeAppointmentId?: number
  ): Observable<AppointmentClash[]>;

  // Patient search — two-step: practice first, then org-wide
  searchPatients(query: string): Observable<QueryPatient[]>;
  searchOrganisationPatients?(query: string): Observable<QueryPatient[]>;

  /**
   * Patient title options (e.g. Mr, Mrs, Dr) for the inline new-patient capture
   * form shown when booking a "new patient" appointment type. Optional — consumers
   * that don't support inline patient creation (e.g. organisation-portal, which has
   * its own appointment dialog) can omit this.
   */
  patientTitles?: Signal<SelectOption[]>;

  /**
   * Quick-create a patient from the calendar's inline new-patient form and return
   * the created patient. App-specific because the create endpoint differs per app.
   * Optional — required only by apps that use the shared dialog's inline new-patient
   * flow. The caller books the appointment against the returned patient id.
   */
  createCalendarPatient?(patient: Patient): Observable<CreatedPatient>;

  // Clone a patient from another practice into the current practice. Returns new patient ID.
  clonePatient?(sourcePracticeId: number, sourcePatientId: number): Observable<number>;

  // WebSocket channel names for real-time updates
  getEventChannelNames(): string[];
}

/**
 * Action handler abstraction for app-specific navigation and actions.
 * Each consuming app provides an implementation.
 */
export interface CalendarActionHandler {
  // Route building — each app has its own calendar route prefix
  buildCalendarRoute(view: string, year: number, month: number, day: number): string;

  // Patient actions
  onViewPatientRequested(patient: CalendarPatient): void;
  /** Open the patient's clinical notes view. Optional — apps without a clinical notes page can omit. */
  onViewPatientClinicalRequested?(patient: CalendarPatient): void;
  /**
   * Request patient creation. The implementation opens a patient form
   * and resolves with the created patient (or null if cancelled).
   * If a patient is returned, the caller is responsible for linking
   * the patient to the appointment via dataProvider.updateAppointment().
   */
  onCreatePatientRequested(appointment: CalendarAppointment): Observable<QueryPatient | null>;

  // Messaging actions — optional; apps that don't support messaging can omit these
  onSendMessageRequested?(patient: CalendarPatient): void;
  onSendEmailRequested?(patient: CalendarPatient): void;
  onSendBulkMessageRequested?(patients: CalendarPatient[]): void;
  onSendBulkEmailRequested?(patients: CalendarPatient[]): void;
}

export const CALENDAR_DATA_PROVIDER = new InjectionToken<CalendarDataProvider>('CalendarDataProvider');
export const CALENDAR_ACTION_HANDLER = new InjectionToken<CalendarActionHandler>('CalendarActionHandler');

/**
 * Optional token for apps that have a tabs bar (e.g. xmedical-app).
 * When provided, the CalendarComponent adjusts its fixed positioning
 * to account for the extra height of the tabs bar.
 * Provide a Signal<boolean> that returns true when tabs are visible.
 */
export const CALENDAR_HAS_TABS = new InjectionToken<Signal<boolean>>('CalendarHasTabs');

/**
 * The user's persisted calendar filter selection. A null id means "All"
 * (no filter) — an explicit, remembered choice, distinct from "nothing
 * saved" which is represented by CalendarSelectionStore.load() returning null.
 */
export interface CalendarSelection {
  locationId: number | null;
  practitionerId: number | null;
}

/**
 * Optional persistence for the calendar's location/practitioner filter.
 * When provided, the CalendarComponent restores the saved selection once
 * its data has loaded (falling back to defaults for any id that no longer
 * exists) and saves on every filter change. When absent, selection is not
 * persisted and the calendar uses its computed defaults as before.
 *
 * The implementation owns scoping/keying (e.g. per practice + user) so the
 * library stays app-agnostic.
 */
export interface CalendarSelectionStore {
  /** The persisted selection for the current scope, or null if nothing is saved. */
  load(): CalendarSelection | null;
  /** Persist the selection for the current scope. */
  save(selection: CalendarSelection): void;
}

export const CALENDAR_SELECTION_STORE = new InjectionToken<CalendarSelectionStore>('CalendarSelectionStore');
